import { useMemo, useRef, useState } from 'react';
import { ScoreRing } from '../components/ScoreRing.jsx';
import { StatCard } from '../components/StatCard.jsx';
import { VerdictBox } from '../components/VerdictBox.jsx';
import { ViewToggle } from '../components/ViewToggle.jsx';
import { CategoryAccordion } from '../components/CategoryAccordion.jsx';
import { FixQueue } from '../components/FixQueue.jsx';
import { ComparisonBanner } from '../components/ComparisonBanner.jsx';
import { HowToReadPanel } from './HowToReadPanel.jsx';
import { severityClass } from '../utils/severity.js';
import { isScoreNeutralCategory, buildWeightRedistributionFootnote } from '../../shared/baselineComparisonDisplay.js';
// LAUNCH-CLOSE POLISH-2: the PDF cover's check-level classifier — one engine
// source for both surfaces (threshold-only, hidden excluded, §A4 split).
import { classifyForCover } from '../../engine/coverClassification.js';

const STATUS_SEVERITY = {
  APPROVED: 'OK',
  'CORRECTIONS REQUIRED': 'WARNING',
  REJECTED: 'CRITICAL',
};

// DC3: exclude hidden / not-applicable categories (e.g. Baseline Alignment when
// no baseline is detected), mirroring the PDF's `allCategories.filter(c => !c.hidden)`
// (category-scores.ejs) so the dashboard's "X critical · Y warnings · Z passed"
// line and "Critical Categories" count match the PDF (7 visible → 3 · 1 · 3).
// Tier cutoffs match the PDF category table (≥80 PASS, ≥55 WARNING, else
// CRITICAL — _category-scores-table.ejs). Exported for tests.
//
// BASELINE-SUB4 (Step-3 review): a VISIBLE score-neutral category (Baseline
// Alignment with a baseline attached — categoryScore 0, effectiveWeight 0) is
// excluded too: its 0 is not a graded score, so counting it as a CRITICAL
// category is the same defect F1 fixed on the PDF category-scores page (which
// renders it with no score and no status pill). Single-file runs are
// unaffected (cat7 is hidden there and already skipped).
/**
 * DASH-POLISH P2/P3: pluralise the category-census "warning" noun only
 * ("1 warning" / "2 warnings"). "critical" and "passed" are invariant per the
 * ruling — a category census, distinct from the narrative's check-level counts.
 * Exported for the component tests.
 */
export function warningLabel(n) {
  return `${n} warning${n === 1 ? '' : 's'}`;
}

export function severityCount(categories) {
  let critical = 0;
  let warning = 0;
  let pass = 0;
  for (const cat of categories || []) {
    if (cat.hidden) continue;
    if (isScoreNeutralCategory(cat)) continue;
    const s = Number(cat.score);
    if (!Number.isFinite(s)) continue;
    if (s < 55) critical++;
    else if (s < 80) warning++;
    else pass++;
  }
  return { critical, warning, pass };
}

/**
 * Health Overview panel. Structure matches docs/reference/CritWise_Dashboard.html:
 *   - .overview-row: .score-card (ring + OVERALL HEALTH / verdict / sev counts)
 *     + .stat-row (4 stat-cards)
 *   - .verdict-box banner (engine text verbatim)
 *   - .view-toggle between Category Scores and Fix Queue
 *   - CategoryAccordion or FixQueue depending on the toggle
 */
export function HealthOverview({ analysis, reportId }) {
  const [view, setView] = useState('scores');
  // SURFACE-COHERE (item C+E): the "How to read this report" panel is collapsed
  // by default; the verdict banner's link opens it and scrolls it into view.
  const [howToReadOpen, setHowToReadOpen] = useState(false);
  const howToReadRef = useRef(null);
  const explainVerdict = () => {
    setHowToReadOpen(true);
    if (howToReadRef.current && typeof howToReadRef.current.scrollIntoView === 'function') {
      howToReadRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const categories = useMemo(() => analysis?.categories || [], [analysis]);
  const projectInfo = analysis?.projectInfo || {};
  const topIssues = useMemo(() => analysis?.topIssues || [], [analysis]);
  const sevCounts = useMemo(() => severityCount(categories), [categories]);
  // LAUNCH-CLOSE POLISH-2: check-level counts from the SAME classifier the PDF
  // cover reads (coverData.js) — flatten is plumbing, the classification is not
  // recomputed here (Rule 12).
  const checkCounts = useMemo(() => {
    const all = categories.flatMap((c) => c.checks || []);
    const cls = classifyForCover(all);
    return {
      critical: cls.critical.count,
      warning: cls.warnings.count,
      passed: cls.passed.count,
      informational: cls.informational.count,
    };
  }, [categories]);

  const verdict = analysis?.verdict;
  const verdictSeverity = verdict ? (STATUS_SEVERITY[verdict.status] || 'WARNING') : 'OK';
  const verdictCls = severityClass(verdictSeverity);

  return (
    <div className="health-overview">
      <section className="overview-row" aria-label="Overall schedule health">
        <div className="score-card">
          <div className="score-ring">
            <ScoreRing score={analysis?.overallScore} color={verdict?.color} />
          </div>
          <div className="score-text">
            <div className="score-meta-label">OVERALL HEALTH</div>
            {verdict?.status && (
              <div className={`score-verdict score-verdict-${verdictCls}`}>
                {verdict.status}
              </div>
            )}
            <div className="score-sub">
              {/* DASH-POLISH P2: "Categories:" prefix disambiguates this
                  category census from the verdict narrative's check-level
                  counts; correct singular/plural on "warning" only. */}
              Categories: {sevCounts.critical} critical · {warningLabel(sevCounts.warning)} · {sevCounts.pass} passed
            </div>
            {/* LAUNCH-CLOSE POLISH-2: check-level strip — the SAME engine
                fields the PDF cover's stat tiles read (classifyForCover,
                threshold-only, hidden excluded — Rule 12: no recount). Both
                surfaces now speak both languages (categories + checks). */}
            <div className="score-sub score-sub-checks">
              Checks: {checkCounts.critical} critical · {warningLabel(checkCounts.warning)} · {checkCounts.passed} passed · {checkCounts.informational} informational
            </div>
            {/* Amendment v3.17 §3 (CERT-FIX-1): §10 stale-schedule notice —
                sentence rendered VERBATIM from the single engine emission
                (projectInfo.staleSchedule, Rule 12); identical on the PDF
                cover (Rule 3). Absent entirely when not triggered. */}
            {projectInfo.staleSchedule?.triggered && (
              <div className="stale-schedule-note" role="note">
                {projectInfo.staleSchedule.message}
              </div>
            )}
          </div>
        </div>
        <div className="stat-row">
          <StatCard
            label="Activities Analysed"
            value={projectInfo.analyzableCount ?? projectInfo.activityCount ?? '—'}
            sub={
              projectInfo.activityCount && projectInfo.analyzableCount
                && projectInfo.activityCount !== projectInfo.analyzableCount
                ? `${projectInfo.activityCount} total · ${projectInfo.analyzableCount} in-scope`
                : undefined
            }
          />
          <StatCard
            label="Relationships"
            value={projectInfo.totalRelationships ?? '—'}
          />
          <StatCard
            label="Critical Categories"
            value={sevCounts.critical}
            /* DASH-POLISH P3: verb form aligned to the strip ("passed", not
               "pass"); "warning" pluralises correctly.
               F-7 (V-11): each census fragment carries its own severity token
               (warnings amber, passed green) rather than the whole line red —
               "2 passed" must never read as a failure. */
            sub={(
              <>
                <span className="stat-sub-warn">{warningLabel(sevCounts.warning)}</span>
                {' · '}
                <span className="stat-sub-pass">{sevCounts.pass} passed</span>
              </>
            )}
          />
          <StatCard
            label="Priority Issues"
            value={topIssues.length}
            sub="Ranked by schedule impact"
          />
        </div>
      </section>

      <VerdictBox
        verdict={verdict}
        onExplain={projectInfo.methodology?.verdictRule ? explainVerdict : undefined}
      />

      <ComparisonBanner analysis={analysis} />

      <ViewToggle
        active={view}
        onChange={setView}
        fixCount={topIssues.length}
      />

      <div className="health-body">
        {view === 'scores' && (
          <>
            <CategoryAccordion
              categories={categories}
              reportId={reportId}
              dcmaPoints={analysis?.dcma?.points}
            />
            {/* A3 (D-DashCatWeight): the shared mode-aware weight-redistribution
                footnote the PDF category-scores page shows — string-identical,
                one helper (Rule 12). */}
            <div className="cat-weight-footnote">
              {buildWeightRedistributionFootnote(analysis)}
            </div>
          </>
        )}
        {view === 'fixqueue' && <FixQueue topIssues={topIssues} />}
      </div>

      {/* SURFACE-COHERE (item C+E): "How to read this report" — the dashboard
          equivalent of the PDF methodology page, rendering the identical engine
          strings (methodologyNote · methodology.intro · families · redistribution
          · verdictRule). After Category Scores / Fix Queue; collapsed by
          default. Rule 12: nothing recomposed here. */}
      <HowToReadPanel
        ref={howToReadRef}
        methodologyNote={projectInfo.methodologyNote}
        methodology={projectInfo.methodology}
        open={howToReadOpen}
        onToggle={() => setHowToReadOpen((o) => !o)}
      />
    </div>
  );
}
